import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, X, Trash2, Eye, Shuffle, Play, Download, Mail, RefreshCw, MoreVertical, Table2, Info, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSub,
  ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger
} from "@/components/ui/context-menu";

const STATUS_COLORS = {
  ready:          { bg: "bg-white",      border: "border-gray-200",      badge: "" },
  processing:     { bg: "bg-blue-50",    border: "border-blue-200",      badge: "bg-blue-100 text-blue-700" },
  completed:      { bg: "bg-green-50",   border: "border-green-200",     badge: "bg-green-100 text-green-700" },
  partial:        { bg: "bg-amber-50",   border: "border-amber-300",     badge: "bg-amber-100 text-amber-700" },
  error:          { bg: "bg-red-50",     border: "border-red-300",       badge: "bg-red-100 text-red-600" },
  email_imported: { bg: "bg-[#e6f2f5]", border: "border-[#006080]/20",  badge: "bg-[#e6f2f5] text-[#006080]" },
};

function SendEmailModal({ file, onClose }) {
  const [emails, setEmails] = useState(file.source_email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const send = async () => {
    setSending(true); setError(""); setResult("");
    try {
      const res = await client.post(`/files/${file._id}/send-email`, { emails });
      setResult(res.data.message);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Send Results via Email</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-500">Send the transformed file (with response columns) to email addresses.</p>
        <div>
          <Label className="text-xs text-gray-500">Email Addresses (comma-separated)</Label>
          <Input className="mt-1" value={emails} onChange={e => setEmails(e.target.value)} placeholder="user@company.com, another@example.com" />
        </div>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        {result && <div className="p-2 bg-green-50 rounded text-green-700 text-sm">{result}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={send} disabled={sending || !emails.trim()}>{sending ? "Sending..." : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SyncPreviewModal({ result, file, onClose, onRunExecution }) {
  const navigate = useNavigate();
  const delta = result.delta || 0;
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta > 0 ? "#059669" : delta < 0 ? "#DC2626" : "#9CA3AF";
  const deltaBg   = delta > 0 ? "#F0FDF4"  : delta < 0 ? "#FEF2F2"  : "#F9FAFB";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={17} className="text-sky-600" />
            Sync Complete — Preview
          </DialogTitle>
        </DialogHeader>

        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-3 my-1">
          {[
            { label: "Before", value: result.before_count, color: "#6B7280", bg: "#F9FAFB" },
            { label: "After",  value: result.after_count,  color: "#006080", bg: "#F0F9FF" },
            { label: delta >= 0 ? "Added" : "Removed", value: Math.abs(delta),
              color: deltaColor, bg: deltaBg,
              icon: <DeltaIcon size={14} style={{ color: deltaColor }} /> },
          ].map(({ label, value, color, bg, icon }) => (
            <div key={label} className="rounded-lg border p-3 text-center" style={{ background: bg }}>
              {icon && <div className="flex justify-center mb-0.5">{icon}</div>}
              <p className="text-2xl font-black" style={{ color }}>{value.toLocaleString()}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 uppercase tracking-wider">{label} rows</p>
            </div>
          ))}
        </div>

        {delta === 0 && (
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 text-center">
            Row count unchanged — the sheet data may have been updated in place.
          </div>
        )}

        {/* Sample rows preview */}
        {result.sample_rows?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              First {result.sample_rows.length} rows (live data)
            </p>
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs" data-testid="sync-preview-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-gray-400 w-8">#</th>
                    {result.columns?.slice(0, 6).map(col => (
                      <th key={col} className="px-2 py-1.5 text-left text-gray-500 font-semibold truncate max-w-[120px]">{col}</th>
                    ))}
                    {(result.columns?.length || 0) > 6 && (
                      <th className="px-2 py-1.5 text-gray-400">+{result.columns.length - 6} more</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {result.sample_rows.map((row, i) => (
                    <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-300 font-mono">{i + 1}</td>
                      {result.columns?.slice(0, 6).map(col => (
                        <td key={col} className="px-2 py-1.5 text-gray-700 truncate max-w-[120px]" title={row[col]}>
                          {row[col] || <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Showing up to 5 rows · {result.after_count} total rows now in staging</p>
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" className="border-[#006080] text-[#006080] hover:bg-[#006080]/5"
            onClick={() => { onClose(); navigate(`/staging/${file._id}`); }}>
            <Eye size={13} className="mr-1.5" /> View All Rows
          </Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white"
            onClick={() => { onClose(); navigate(`/execute/${file._id}`); }}
            data-testid="sync-preview-run-btn">
            <Play size={13} className="mr-1.5" /> Run Execution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UploadPage() {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState("");
  const [headerRow, setHeaderRow] = useState(0);
  const [delimiter, setDelimiter] = useState("");
  const [sendEmailFile, setSendEmailFile] = useState(null);
  const [showGSheets, setShowGSheets] = useState(false);
  const [gsUrl, setGsUrl] = useState("");
  const [gsImporting, setGsImporting] = useState(false);
  const [gsError, setGsError] = useState("");
  const [syncingFile, setSyncingFile] = useState(null);
  const [syncPreviewResult, setSyncPreviewResult] = useState(null);
  const [syncPreviewFile, setSyncPreviewFile] = useState(null);
  const fileInputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    loadFiles();
    client.get("/templates/").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const loadFiles = () => {
    client.get("/files/").then(r => setFiles(r.data)).catch(() => {});
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} file(s) and all their data?`)) return;
    setBulkDeleting(true);
    try {
      await client.post("/files/bulk-delete", { ids: [...selected] });
      setSelected(new Set());
      loadFiles();
    } finally { setBulkDeleting(false); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) { setError("Only .xlsx, .xls, .csv supported."); return; }
    if (file.size > 50 * 1024 * 1024) { setError("File too large (max 50 MB)."); return; }
    setError(""); setUploading(true); setUploadProgress("Uploading...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("header_row", headerRow);
      if (delimiter) fd.append("delimiter", delimiter);
      const res = await client.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadProgress("Done!");
      loadFiles();
      setTimeout(() => { setUploading(false); setUploadProgress(null); navigate(`/staging/${res.data.upload_id}`); }, 500);
    } catch (err) { setError(formatApiError(err)); setUploading(false); setUploadProgress(null); }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [headerRow, delimiter]);

  const deleteFile = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this file?")) return;
    await client.delete(`/files/${id}`);
    loadFiles();
  };

  const applyTemplate = async (uploadId, templateId) => {
    await client.post(`/templates/${templateId}/apply`, { upload_id: uploadId });
    navigate(`/staging/${uploadId}`);
  };

  const importFromGSheets = async () => {
    if (!gsUrl.trim()) { setGsError("Paste a Google Sheets URL."); return; }
    setGsImporting(true); setGsError("");
    try {
      const res = await client.post("/google-sheets/import", { url: gsUrl });
      loadFiles();
      setShowGSheets(false); setGsUrl("");
      navigate(`/staging/${res.data.upload_id}`);
    } catch (e) {
      setGsError(e?.response?.data?.detail || "Import failed. Check the URL and permissions.");
    } finally { setGsImporting(false); }
  };

  const syncGSheet = async (file, e) => {
    e.stopPropagation();
    setSyncingFile(file._id);
    try {
      const res = await client.post(`/google-sheets/sync/${file._id}`);
      loadFiles();
      setSyncPreviewFile(file);
      setSyncPreviewResult(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Sync failed");
    } finally { setSyncingFile(null); }
  };

  const getStatusStyle = (file) => STATUS_COLORS[file.status] || STATUS_COLORS.ready;

  return (
    <div className="p-6 max-w-4xl mx-auto fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Upload File</h1>
        <p className="text-gray-500 text-sm mt-0.5">Upload an Excel or CSV file, or import directly from Google Sheets</p>
      </div>

      {syncPreviewResult && syncPreviewFile && (
        <SyncPreviewModal
          result={syncPreviewResult}
          file={syncPreviewFile}
          onClose={() => { setSyncPreviewResult(null); setSyncPreviewFile(null); }}
        />
      )}

      {/* Google Sheets dialog */}
      <Dialog open={showGSheets} onOpenChange={setShowGSheets}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 size={18} className="text-[#006080]" />
              Import from Google Sheets
              <div className="relative inline-block group cursor-help ml-0.5">
                <Info size={13} className="text-gray-400 hover:text-[#006080]" />
                <div className="absolute z-50 bottom-full left-0 mb-2 w-72 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
                  Paste the full Google Sheets URL. Public sheets work instantly. For private sheets, configure a Google Service Account in Admin Settings → Google Sheets tab first — then share the sheet with that account's email.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-gray-500">Google Sheets URL</Label>
              <Input
                className="mt-1 font-mono text-sm"
                value={gsUrl}
                onChange={e => setGsUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                data-testid="gsheets-url-input"
              />
            </div>
            {gsError && <p className="text-xs text-red-600 bg-red-50 p-2 rounded">{gsError}</p>}
            <p className="text-xs text-gray-400">The first sheet will be imported by default. Headers from row 1.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGSheets(false); setGsError(""); }}>Cancel</Button>
            <Button className="bg-[#006080] text-white hover:bg-[#004d66]" onClick={importFromGSheets} disabled={gsImporting} data-testid="gsheets-import-btn">
              {gsImporting ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Header Row</label>
          <input type="number" min={0} value={headerRow} onChange={e => setHeaderRow(parseInt(e.target.value) || 0)} className="w-16 h-7 px-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#006080]" data-testid="header-row-input" />
          <span className="text-xs text-gray-400">(0 = first row)</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">CSV Delimiter</label>
          <input value={delimiter} onChange={e => setDelimiter(e.target.value)} placeholder="auto" maxLength={1} className="w-14 h-7 px-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#006080]" />
          <span className="text-xs text-gray-400">(blank = auto-detect)</span>
        </div>
      </div>

      <div
        onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200 mb-3 ${dragging ? "border-[#006080] bg-[#e6f2f5]" : "border-gray-200 bg-white hover:border-[#006080] hover:bg-[#F0F8FA]"} ${uploading ? "cursor-not-allowed opacity-75" : ""}`}
        data-testid="upload-dropzone"
      >
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => handleFile(e.target.files[0])} data-testid="file-input" />
        {uploading ? (
          <div className="space-y-3"><div className="w-10 h-10 border-2 border-[#006080] border-t-transparent rounded-full animate-spin mx-auto" /><p className="text-[#006080] font-semibold text-sm">{uploadProgress}</p></div>
        ) : (
          <>
            <Upload size={36} className={`mx-auto mb-3 ${dragging ? "text-[#006080]" : "text-gray-300"}`} />
            <p className="text-gray-700 font-semibold">Drop your file here, or click to browse</p>
            <p className="text-gray-400 text-sm mt-1">Supports .xlsx, .xls, .csv — up to 50 MB</p>
          </>
        )}
      </div>
      <div className="flex justify-center mb-5">
        <Button variant="outline" className="text-sm gap-2 border-[#006080] text-[#006080] hover:bg-[#006080]/5" onClick={() => setShowGSheets(true)} data-testid="import-gsheets-btn">
          <Table2 size={15} /> Import from Google Sheets
          <div className="relative inline-block group cursor-help">
            <Info size={12} className="text-gray-400 hover:text-[#006080]" />
            <div className="absolute z-50 bottom-full right-0 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
              Import rows directly from a Google Sheet URL. Public sheets work without setup. For private sheets, configure a service account in Admin → Google Sheets.
            </div>
          </div>
        </Button>
      </div>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="upload-error">{error}</div>}

      {files.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Upload History</p>
            <div className="flex gap-2">
              {selected.size > 0 && (
                <Button size="sm" variant="outline" className="h-6 text-xs text-red-500 hover:bg-red-50 hover:border-red-200" onClick={bulkDelete} disabled={bulkDeleting}>
                  <Trash2 size={11} className="mr-1" />{bulkDeleting ? "Deleting..." : `Delete ${selected.size}`}
                </Button>
              )}
              <button className="text-gray-400 hover:text-[#006080]" onClick={loadFiles}><RefreshCw size={14} /></button>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {files.map(file => {
              const style = getStatusStyle(file);
              return (
                <ContextMenu key={file._id}>
                  <ContextMenuTrigger>
                    <div
                      className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors ${style.bg} border-l-2 ${style.border} ${selected.has(file._id) ? "!bg-[#F0F8FA]" : ""}`}
                      data-testid={`file-row-${file._id}`}
                    >
                      <input type="checkbox" className="w-4 h-4 rounded flex-shrink-0" checked={selected.has(file._id)} onChange={() => toggleSelect(file._id)} onClick={e => e.stopPropagation()} />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/staging/${file._id}`)}>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-800 truncate">{file.filename}</p>
                          {file.status && file.status !== "ready" && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${style.badge}`}>
                              {file.status === "partial" ? "⚠ partial" : file.status === "error" ? "✕ failed" : file.status}
                            </span>
                          )}
                          {file.source_type === "google_sheets" && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-sky-100 text-sky-700">📊 Google Sheet</span>}
                          {file.source === "email" && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-[#e6f2f5] text-[#006080]">email</span>}
                        </div>
                        <p className="text-xs text-gray-400">
                          {file.row_count?.toLocaleString()} rows · {file.columns?.length} cols ·{" "}
                          {file.created_at ? new Date(file.created_at).toLocaleDateString() : ""}
                          {file.last_synced_at && <span> · synced {new Date(file.last_synced_at).toLocaleDateString()}</span>}
                          {file.source_email && <span> · from {file.source_email}</span>}
                        </p>
                        {file.error_message && <p className="text-xs text-red-500 truncate mt-0.5">{file.error_message}</p>}
                      </div>
                      {file.pipeline?.length > 0 && <Badge variant="secondary" className="text-xs bg-[#e6f2f5] text-[#006080] border-0">{file.pipeline.length} transforms</Badge>}
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#006080]" onClick={() => navigate(`/staging/${file._id}`)} title="View"><Eye size={15} /></button>
                        <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#006080]" onClick={() => navigate(`/transform/${file._id}`)} title="Transform"><Shuffle size={15} /></button>
                        <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#006080]" onClick={() => navigate(`/execute/${file._id}`)} title="Execute"><Play size={15} /></button>
                        {file.source_type === "google_sheets" && (
                          <button
                            className="p-1.5 rounded hover:bg-sky-50 text-gray-400 hover:text-sky-600 flex items-center gap-1"
                            onClick={(e) => syncGSheet(file, e)}
                            title="Sync & Preview latest data from Google Sheets"
                            data-testid={`sync-gsheet-${file._id}`}
                          >
                            <RefreshCw size={15} className={syncingFile === file._id ? "animate-spin text-sky-600" : ""} />
                            <span className="text-[10px] font-medium text-sky-600 hidden sm:inline">
                              {syncingFile === file._id ? "Syncing..." : "Sync & Preview"}
                            </span>
                          </button>
                        )}
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file._id}/download-xlsx`} download onClick={e => e.stopPropagation()} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#006080]" title="Download XLSX (transformed + responses)"><Download size={15} /></a>
                        <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#006080]" onClick={() => setSendEmailFile(file)} title="Send via email"><Mail size={15} /></button>
                        <button className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500" onClick={e => deleteFile(file._id, e)} title="Delete" data-testid={`delete-file-${file._id}`}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => navigate(`/staging/${file._id}`)}>View Data</ContextMenuItem>
                    <ContextMenuItem onClick={() => navigate(`/transform/${file._id}`)}>Transform</ContextMenuItem>
                    <ContextMenuItem onClick={() => navigate(`/execute/${file._id}`)}>Execute API</ContextMenuItem>
                    <ContextMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${file._id}/download-xlsx`, "_blank")}>Download XLSX</ContextMenuItem>
                    <ContextMenuItem onClick={() => setSendEmailFile(file)}>Send via Email</ContextMenuItem>
                    {templates.length > 0 && (
                      <ContextMenuSub>
                        <ContextMenuSubTrigger>Run with Template</ContextMenuSubTrigger>
                        <ContextMenuSubContent>{templates.map(t => <ContextMenuItem key={t._id} onClick={() => applyTemplate(file._id, t._id)}>{t.name}</ContextMenuItem>)}</ContextMenuSubContent>
                      </ContextMenuSub>
                    )}
                    <ContextMenuItem className="text-red-500" onClick={e => deleteFile(file._id, e)}>Delete</ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              );
            })}
          </div>
        </div>
      )}

      {sendEmailFile && <SendEmailModal file={sendEmailFile} onClose={() => setSendEmailFile(null)} />}
    </div>
  );
}
